import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { actionAddContactUs } from "../services/settingsServices";
import Utils from "../utils";
import useSeo, { i18nToOgLocale } from "../utils/useSeo";
import "../asset/style/landing.css";

const LANGS = [
  { code: "pt", label: "PT", flag: "🇵🇹" },
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "es", label: "ES", flag: "🇪🇸" },
  { code: "fr", label: "FR", flag: "🇫🇷" },
  { code: "de", label: "DE", flag: "🇩🇪" },
  { code: "it", label: "IT", flag: "🇮🇹" },
];

const LandingPage = () => {
  const year = new Date().getFullYear();
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", company: "", mobile: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const currentLang = (i18n.language || "pt").substring(0, 2).toUpperCase();

  useSeo({
    title: "Gestão de Centros de Abate Automóvel",
    description:
      "O software que simplifica a gestão dos centros de abate automóvel. Gestão viaturas, VFV, peças, resíduos, despoluição, etiquetas, sincronização marketplaces, vendas num só sítio. Mais de 30 empresas em Portugal já confiam em nós.",
    keywords:
      "abate automóvel, centro de desmantelamento, software peças usadas, inventário auto, depoluição LER, gestão stock automóvel, peças recicladas, Moloni, ERP automóvel, ELV, reciclagem viaturas",
    path: "/",
    image: "https://tp.software/libs/assets/images/logo.png",
    locale: i18nToOgLocale(i18n.language),
  });

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("lng", code);
    setLangOpen(false);
  };

  const calendarUrl = (() => {
    const title = encodeURIComponent("TP Software - Expomecânica 2026 - Exponor Porto");
    const details = encodeURIComponent("Visite-nos no Stand 5D01, Entrada Pavilhão 5. https://pt.telepecas.com/expomecanica2026");
    const location = encodeURIComponent("Exponor - Feira Internacional do Porto, Av. Dr. António Macedo, 4450-617 Leça da Palmeira");
    const dates = "20260529T090000Z/20260531T180000Z";
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&location=${location}&dates=${dates}`;
  })();

  return (
    <div className="tp-landing">
      {/* Announcement bar */}
      <div className="tp-announcement">
        <span className="tp-ann-dot" />
        <strong>{t("landing_announcement_new")}</strong>
        <span className="tp-ann-sep">·</span>
        {t("landing_announcement_launch", { year })}
      </div>

      {/* Nav */}
      <header className="tp-nav">
        <div className="tp-nav-inner">
          <a href="/" className="tp-brand">
            <img
              src="https://tp.software/libs/images/tpsoftware-green-white.png"
              alt="TP Software"
              className="tp-brand-logo"
            />
          </a>
          <nav className="tp-nav-links">
            <a href="#features">{t("landing_nav_features")}</a>
            <a href="#stats">{t("landing_nav_customers")}</a>
            <a href="#api">{t("landing_nav_api")}</a>
            <a href="#expomecanica">{t("landing_nav_expo")}</a>
            <a href="#contacto">{t("landing_nav_contact")}</a>
          </nav>
          <div className="tp-nav-cta">
            {/* Language selector */}
            <div className="tp-lang-wrap">
              <button
                className="tp-lang-btn"
                onClick={() => setLangOpen(!langOpen)}
              >
                🌐 {currentLang} ▾
              </button>
              {langOpen && (
                <div className="tp-lang-dropdown">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      className={`tp-lang-opt${i18n.language?.startsWith(l.code) ? " active" : ""}`}
                      onClick={() => changeLang(l.code)}
                    >
                      <span className="tp-lang-flag">{l.flag}</span>
                      <span>{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Link to="/login" className="tp-btn-ghost">{t("landing_btn_login")}</Link>
            <Link to="/signup" className="tp-btn-primary">{t("landing_btn_signup")}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="tp-hero">
        <div className="tp-hero-bg-grid" />
        {/* Animated background: purple gradient blobs + floating sparkles */}
        <div className="tp-hero-aurora" aria-hidden="true">
          <span className="tp-aurora tp-aurora-1" />
          <span className="tp-aurora tp-aurora-2" />
          <span className="tp-aurora tp-aurora-3" />
        </div>
        <div className="tp-hero-sparkles" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className={`tp-spark tp-spark-${i + 1}`} />
          ))}
        </div>

        {/* Animated car racing across with gradient trail */}
        <div className="tp-hero-car-lane" aria-hidden="true">
          <div className="tp-hero-car-trail" />
          <svg
            className="tp-hero-car"
            viewBox="0 0 240 96"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="tpCarBody" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#A78BFA" />
                <stop offset="100%" stopColor="#6D28D9" />
              </linearGradient>
              <linearGradient id="tpCarGlass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E9D5FF" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.6" />
              </linearGradient>
              <radialGradient id="tpCarLight" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#FDE68A" stopOpacity="1" />
                <stop offset="60%" stopColor="#F59E0B" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* Car shadow */}
            <ellipse cx="120" cy="86" rx="88" ry="4" fill="#000" opacity="0.35" />
            {/* Body */}
            <path
              d="M18 66 C 18 52, 38 40, 60 38 L 90 26 C 102 20, 128 20, 148 26 L 180 38 C 206 40, 226 52, 226 66 L 226 74 C 226 78, 222 82, 218 82 L 200 82 C 198 72, 188 64, 176 64 C 164 64, 154 72, 152 82 L 92 82 C 90 72, 80 64, 68 64 C 56 64, 46 72, 44 82 L 26 82 C 22 82, 18 78, 18 74 Z"
              fill="url(#tpCarBody)"
            />
            {/* Window */}
            <path
              d="M72 40 L 96 30 C 106 26, 126 26, 140 30 L 166 40 L 148 52 L 88 52 Z"
              fill="url(#tpCarGlass)"
              opacity="0.85"
            />
            {/* Door line */}
            <path d="M120 32 L 120 52" stroke="#4C1D95" strokeWidth="1.2" opacity="0.5" />
            {/* Front headlight */}
            <circle cx="220" cy="60" r="10" fill="url(#tpCarLight)" />
            <circle cx="220" cy="60" r="3.5" fill="#FEF3C7" />
            {/* Rear light */}
            <rect x="20" y="56" width="8" height="6" rx="2" fill="#EF4444" opacity="0.9" />
            {/* Wheels */}
            <circle cx="68" cy="82" r="12" fill="#111827" />
            <circle cx="68" cy="82" r="6" fill="#374151" />
            <circle cx="68" cy="82" r="2.5" fill="#A78BFA" />
            <circle cx="176" cy="82" r="12" fill="#111827" />
            <circle cx="176" cy="82" r="6" fill="#374151" />
            <circle cx="176" cy="82" r="2.5" fill="#A78BFA" />
          </svg>
        </div>

        <div className="tp-hero-content">
          <div className="tp-rating">
            <span className="tp-stars">★★★★★</span>
            <span>{t("landing_hero_rating")}</span>
          </div>
          <h1 className="tp-hero-title">
            {t("landing_hero_title_1")}{" "}
            <span className="tp-accent">{t("landing_hero_title_accent")}</span>
            {t("landing_hero_title_2")}
          </h1>
          <p className="tp-hero-sub">{t("landing_hero_sub")}</p>
          <div className="tp-hero-ctas">
            <Link to="/signup" className="tp-btn-primary-lg">
              {t("landing_hero_cta_signup")}
            </Link>
            <a href="#features" className="tp-btn-ghost-lg">
              <span className="tp-play">▶</span> {t("landing_hero_cta_features")}
            </a>
          </div>

          {/* Floating cards + phone mockup */}
          <div className="tp-hero-showcase">
            <div className="tp-float tp-float-tl">
              <div className="tp-float-icon" style={{ background: "#1e3a8a" }}>🚗</div>
              <div>
                <div className="tp-float-label">{t("landing_hero_card_vehicles")}</div>
                <div className="tp-float-value">25,451</div>
              </div>
            </div>

            <div className="tp-float tp-float-tr">
              <div className="tp-float-icon" style={{ background: "var(--accent)" }}>📦</div>
              <div>
                <div className="tp-float-label">{t("landing_hero_card_parts")}</div>
                <div className="tp-float-value">1,171,592</div>
              </div>
            </div>

            <div className="tp-phone">
              <div className="tp-phone-screen">
                <div className="tp-phone-header">
                  <span className="tp-phone-title">{t("landing_hero_phone_dashboard")}</span>
                  <span className="tp-phone-chip">{t("landing_hero_phone_today")}</span>
                </div>
                <div className="tp-phone-metric">
                  <div className="tp-phone-metric-label">{t("landing_hero_phone_metric")}</div>
                  <div className="tp-phone-metric-value">€ 184.300</div>
                  <div className="tp-phone-trend">{t("landing_hero_phone_trend")}</div>
                </div>
                <div className="tp-phone-bars">
                  <div className="tp-bar" style={{ height: "40%" }} />
                  <div className="tp-bar" style={{ height: "65%" }} />
                  <div className="tp-bar" style={{ height: "50%" }} />
                  <div className="tp-bar" style={{ height: "85%" }} />
                  <div className="tp-bar" style={{ height: "70%" }} />
                  <div className="tp-bar" style={{ height: "95%" }} />
                  <div className="tp-bar" style={{ height: "78%" }} />
                </div>
                <div className="tp-phone-list-title">{t("landing_hero_phone_list")}</div>
                <div className="tp-phone-list-item">
                  <span>{t("landing_hero_phone_item_1")}</span>
                  <span className="tp-chip tp-chip-green">{t("landing_hero_phone_chip_available")}</span>
                </div>
                <div className="tp-phone-list-item">
                  <span>{t("landing_hero_phone_item_2")}</span>
                  <span className="tp-chip tp-chip-purple">{t("landing_hero_phone_chip_reserved")}</span>
                </div>
              </div>
            </div>

            <div className="tp-float tp-float-bl">
              <div className="tp-float-icon" style={{ background: "#10b981" }}>♻️</div>
              <div>
                <div className="tp-float-label">{t("landing_hero_card_depollution")}</div>
                <div className="tp-float-value">8,291</div>
              </div>
            </div>

            <div className="tp-float tp-float-br">
              <div className="tp-float-icon" style={{ background: "#3b82f6" }}>🏷️</div>
              <div>
                <div className="tp-float-label">{t("landing_hero_card_labels")}</div>
                <div className="tp-float-value">243,122</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section id="partners" className="tp-partners">
        <div className="tp-partners-inner">
          <span className="tp-section-tag">{t("landing_partners_tag")}</span>
          <h2>{t("landing_partners_title")}</h2>
          <p>{t("landing_partners_desc")}</p>
          <div className="tp-partners-grid">
            {[
              { url: "https://www.valorcar.pt/pt", img: "https://tp.software/libs/assets/partners/valorcar/logo.png", name: "Valorcar" },
              { url: "https://www.tecalliance.net/", img: "https://tp.software/libs/assets/partners/tecalliance/tecAlliance-logo.svg", name: "TecAlliance" },
              { url: "https://moloni.pt/", img: "https://tp.software/libs/assets/partners/moloni/moloni-logo.svg", name: "Moloni" },
              { url: "https://www.b-parts.com/", img: "https://tp.software/libs/assets/partners/bparts/logo.svg", name: "B-Parts" },
              { url: "https://www.recambiofacil.com/", img: "https://tp.software/libs/assets/partners/recambiofacil/logo.png", name: "Recambiofácil" },
              { url: "https://pt.ebay.com/", img: "https://tp.software/libs/assets/partners/ebay/logo.svg", name: "eBay" },
              { url: "https://custojusto.pt", img: "https://tp.software/libs/assets/partners/custojusto/logo.png", name: "CustoJusto" },
              { url: "https://ovoko.fr/", img: "https://tp.software/libs/assets/partners/ovoko/logo.png", name: "Ovoko" },
              { url: "https://www.azelerecambios.com/", img: "https://tp.software/libs/assets/partners/azeler/logo_azeler.png", name: "Azeler" },
              { url: "https://ecooparts.com/", img: "https://tp.software/libs/assets/partners/ecooparts/logo.png", name: "Ecooparts" },
              { url: "https://pt.telepecas.com", img: "https://tp.software/libs/assets/partners/telepecas/logo.png", name: "TelePeças" },
            ].map((p) => (
              <a key={p.url} href={p.url} target="_blank" rel="noreferrer" className="tp-partner">
                <img src={p.img} alt={p.name} />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Expomecânica section */}
      <section id="expomecanica" className="tp-expo">
        <div className="tp-expo-inner">
          <div className="tp-expo-text">
            <span className="tp-expo-tag">{t("landing_expo_tag")}</span>
            <h2>{t("landing_expo_title")}</h2>
            <p>{t("landing_expo_desc")}</p>
            <div className="tp-expo-location">
              <div className="tp-expo-pin">📍</div>
              <div>
                <div className="tp-expo-where">{t("landing_expo_location")}</div>
                <div className="tp-expo-sub">{t("landing_expo_date")}</div>
              </div>
            </div>
            <div className="tp-expo-actions">
              <a
                href="https://pt.telepecas.com/expomecanica2026"
                target="_blank"
                rel="noreferrer"
                className="tp-btn-primary-lg"
              >
                {t("landing_expo_btn_invite")}
              </a>
              <a
                href={calendarUrl}
                target="_blank"
                rel="noreferrer"
                className="tp-btn-ghost-lg"
              >
                {t("landing_expo_btn_calendar")}
              </a>
            </div>
          </div>
          <a
            href="https://pt.telepecas.com/expomecanica2026"
            target="_blank"
            rel="noreferrer"
            className="tp-expo-banner"
          >
            <img
              src="https://pt.telepecas.com/libs/images/expomecanica2026/banner.png"
              alt="Expomecânica 2026"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </a>
        </div>
      </section>

      {/* Stats */}
      <section id="stats" className="tp-stats">
        <div className="tp-stats-inner">
          <div className="tp-stat">
            <div className="tp-stat-num">+30</div>
            <div className="tp-stat-label">{t("landing_stat_centers")}</div>
          </div>
          <div className="tp-stat">
            <div className="tp-stat-num">+1M</div>
            <div className="tp-stat-label">{t("landing_stat_parts")}</div>
          </div>
          <div className="tp-stat">
            <div className="tp-stat-num">25.000</div>
            <div className="tp-stat-label">{t("landing_stat_vehicles")}</div>
          </div>
          <div className="tp-stat">
            <div className="tp-stat-num">99,9%</div>
            <div className="tp-stat-label">{t("landing_stat_uptime")}</div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="tp-features">
        <div className="tp-features-inner">
          <div className="tp-features-header">
            <span className="tp-section-tag">{t("landing_features_tag")}</span>
            <h2>{t("landing_features_title")}</h2>
            <p>{t("landing_features_desc")}</p>
          </div>
          <div className="tp-features-grid">
            <Feature icon="🚗" title={t("landing_feature_vehicles_title")} desc={t("landing_feature_vehicles_desc")} />
            <Feature icon="📦" title={t("landing_feature_inventory_title")} desc={t("landing_feature_inventory_desc")} />
            <Feature icon="♻️" title={t("landing_feature_depollution_title")} desc={t("landing_feature_depollution_desc")} />
            <Feature icon="🏷️" title={t("landing_feature_labels_title")} desc={t("landing_feature_labels_desc")} />
            <Feature icon="💳" title={t("landing_feature_sales_title")} desc={t("landing_feature_sales_desc")} />
            <Feature icon="🔌" title={t("landing_feature_api_title")} desc={t("landing_feature_api_desc")} />
          </div>
          <div className="tp-features-coming">
            <span className="tp-features-coming-dot" />
            {t("landing_features_coming")}
          </div>
        </div>
      </section>

      {/* API section */}
      <section id="api" className="tp-api">
        <div className="tp-api-inner">
          <div className="tp-api-text">
            <span className="tp-section-tag">{t("landing_api_tag")}</span>
            <h2>{t("landing_api_title")}</h2>
            <p>{t("landing_api_desc")}</p>
            <div className="tp-api-features">
              <div className="tp-api-feature">
                <span className="tp-api-feature-icon">✓</span>
                <span>{t("landing_api_feature_1")}</span>
              </div>
              <div className="tp-api-feature">
                <span className="tp-api-feature-icon">✓</span>
                <span>{t("landing_api_feature_2")}</span>
              </div>
              <div className="tp-api-feature">
                <span className="tp-api-feature-icon">✓</span>
                <span>{t("landing_api_feature_3")}</span>
              </div>
            </div>
            <Link to="/api-docs" className="tp-btn-primary-lg">
              {t("landing_api_btn_docs")}
            </Link>
          </div>
          <div className="tp-api-code">
            <div className="tp-api-code-header">
              <span className="tp-api-code-dot red" />
              <span className="tp-api-code-dot yellow" />
              <span className="tp-api-code-dot green" />
              <span className="tp-api-code-title">terminal · curl</span>
            </div>
            <div>
              <span className="tp-api-comment">{t("landing_api_code_comment_1")}</span>
            </div>
            <div>
              <span className="tp-api-method">GET</span>{" "}
              <span className="tp-api-str">/api/v1/ecommerce-get-product-inventory</span>
            </div>
            <div style={{ marginTop: 10 }}>
              <span className="tp-api-comment">{t("landing_api_code_comment_2")}</span>
            </div>
            <div>
              <span className="tp-api-key">Authorization</span>: Bearer{" "}
              <span className="tp-api-str">{"<token>"}</span>
            </div>
            <div>
              <span className="tp-api-key">Content-Type</span>:{" "}
              <span className="tp-api-str">application/json</span>
            </div>
            <div style={{ marginTop: 10 }}>
              <span className="tp-api-comment">{t("landing_api_code_comment_3")}</span>
            </div>
            <div>
              <span className="tp-api-key">?limit</span>=50
              <span className="tp-api-key">&page</span>=1
              <span className="tp-api-key">&search</span>=
              <span className="tp-api-str">farol</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section id="contacto" className="tp-contact">
        <div className="tp-contact-inner">
          <div className="tp-contact-text">
            <span className="tp-section-tag">{t("landing_contact_tag")}</span>
            <h2>{t("landing_contact_title")}</h2>
            <p>{t("landing_contact_desc")}</p>
            <div className="tp-contact-info">
              <div className="tp-contact-row">
                <span>📧</span>
                <a href="mailto:geral@tp.software">geral@tp.software</a>
              </div>
              <div className="tp-contact-row">
                <span>📞</span>
                <a href="tel:+351253600550">+351 253 600 550</a>
              </div>
            </div>
          </div>
          <form
            className="tp-contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (contactLoading) return;
              setContactLoading(true);
              actionAddContactUs(contactForm)
                .then(() => {
                  toast.success(t("landing_contact_success"));
                  setContactForm({ name: "", email: "", company: "", mobile: "", message: "" });
                })
                .catch((err) => toast.error(Utils.getErrorMessage(err)))
                .finally(() => setContactLoading(false));
            }}
          >
            <div className="tp-form-row">
              <input type="text" placeholder={t("landing_contact_placeholder_name")} required className="tp-input"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} />
              <input type="email" placeholder={t("landing_contact_placeholder_email")} required className="tp-input"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
            </div>
            <input type="text" placeholder={t("landing_contact_placeholder_company")} className="tp-input"
              value={contactForm.company}
              onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} />
            <input type="tel" placeholder={t("landing_contact_placeholder_phone")} required className="tp-input"
              value={contactForm.mobile}
              onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })} />
            <textarea placeholder={t("landing_contact_placeholder_message")} rows={4} required className="tp-input tp-textarea"
              value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} />
            <button type="submit" className="tp-btn-primary-lg tp-form-submit" disabled={contactLoading}>
              {contactLoading ? t("landing_contact_btn_sending") : t("landing_contact_btn_send")}
            </button>
          </form>
        </div>
      </section>

      {/* CTA */}
      <section className="tp-cta">
        <div className="tp-cta-inner">
          <h2>{t("landing_cta_title")}</h2>
          <p>{t("landing_cta_desc")}</p>
          <div className="tp-cta-actions">
            <Link to="/signup" className="tp-btn-primary-lg">
              {t("landing_cta_signup")}
            </Link>
            <Link to="/login" className="tp-btn-ghost-lg">
              {t("landing_cta_login")}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="tp-footer">
        <div className="tp-footer-inner">
          <div className="tp-footer-brand">
            <a href="/" className="tp-brand">
              <img
                src="https://tp.software/libs/images/tpsoftware-green-white.png"
                alt="TP Software"
                className="tp-brand-logo"
              />
            </a>
            <p>{t("landing_footer_desc")}</p>
          </div>
          <div className="tp-footer-cols">
            <div>
              <h4>{t("landing_footer_product")}</h4>
              <a href="#features">{t("landing_nav_features")}</a>
              <a href="#stats">{t("landing_nav_customers")}</a>
              <a href="#expomecanica">{t("landing_nav_expo")}</a>
            </div>
            <div>
              <h4>{t("landing_footer_legal")}</h4>
              <Link to="/terms-condition">{t("landing_footer_terms")}</Link>
              <Link to="/privacy-policy">{t("landing_footer_privacy")}</Link>
              <Link to="/faq">{t("landing_footer_faq")}</Link>
            </div>
            <div>
              <h4>{t("landing_footer_account")}</h4>
              <Link to="/login">{t("landing_btn_login")}</Link>
              <Link to="/signup">{t("landing_btn_signup")}</Link>
            </div>
          </div>
        </div>
        <div className="tp-footer-bottom">
          {t("landing_footer_copyright", { year })}
        </div>
      </footer>
    </div>
  );
};

const Feature = ({ icon, title, desc, badge }) => (
  <div className="tp-feature">
    <div className="tp-feature-icon">{icon}</div>
    <div className="tp-feature-body">
      <h3>
        {title}
        {badge && <span className="tp-feature-badge">{badge}</span>}
      </h3>
      <p>{desc}</p>
    </div>
  </div>
);

export default LandingPage;
